// src/views/Login/ForgotPassword.tsx

import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import i18n from '@/shared/i18n/i18n';
import AuthLayout from '@/components/AppComponents/Layout/AuthLayout';
import {
  ForgotPasswordFormValues,
  forgotPasswordSchema,
} from './schemas/forgotPassword.schema';
import { useForgotPasswordUseCase } from '@/domains/auth/auth.usecases';
import { Form } from '@/components/AppComponents/Form/Form';
import { Button } from '@/components/UI/components/Button';
import { TextInput } from '@/components/UI/components/Textfield';
import { NAVBAR_SOURCES } from '@/components/AppComponents/Sidebar/NavBar/NavBarSources';
import { showErrorToast } from '@/shared/notifications/toast';
import { Callout } from '@/components/UI/components/Callout';
import { RiErrorWarningLine } from '@remixicon/react';

const ForgotPassword = () => {
  const [isSent, setIsSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const message = location.state?.message;
  const forgotPasswordUseCase = useForgotPasswordUseCase();

  useEffect(() => {
    if (message) {
      showErrorToast(message);
    }
  }, [message]);

  const recoveryMethods = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const handleForgotPassword = async (data: ForgotPasswordFormValues) => {
    try {
      setIsLoading(true);
      await forgotPasswordUseCase(data);
      setIsSent(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout title={i18n.t('forgotPassword.title')}>
      <div className="flex flex-col justify-between space-y-8">
        {isSent ? (
          <div className="flex flex-col justify-start items-baseline space-y-8 text-gray-200">
            <p>{i18n.t('forgotPassword.successMessageStep1')}</p>
            <Button
              variant="primary"
              onClick={() => navigate(NAVBAR_SOURCES.login)}
              className="w-full"
            >
              {i18n.t('forgotPassword.back')}
            </Button>
          </div>
        ) : (
          <div className="space-y-4 text-gray-200">
            {message && (
              <Callout title={message} icon={RiErrorWarningLine} color="red" />
            )}
            <p>{i18n.t('forgotPassword.leadIn')}</p>
            <Form<ForgotPasswordFormValues>
              onSubmit={handleForgotPassword}
              methods={recoveryMethods}
            >
              <div className="flex flex-col justify-start items-baseline space-y-8">
                <div className="flex flex-col justify-start items-baseline space-y-2 w-full">
                  <label
                    htmlFor="email"
                    className="label pl-1 font-accent text-gray-200"
                  >
                    {i18n.t('forgotPassword.email')}
                  </label>
                  <TextInput
                    type="email"
                    placeholder="user@example.com"
                    {...recoveryMethods.register('email')}
                    error={!!recoveryMethods.formState.errors.email}
                    errorMessage={
                      recoveryMethods.formState.errors.email?.message
                    }
                  />
                </div>
                <Button
                  type="submit"
                  loading={isLoading}
                  variant="primary"
                  className="w-full"
                >
                  {i18n.t('forgotPassword.submit')}
                </Button>
              </div>
            </Form>
          </div>
        )}
      </div>
    </AuthLayout>
  );
};

export default ForgotPassword;
